import React, { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Home, Users, Activity, Megaphone, MapPin, Heart, Settings, Download,
  FileSpreadsheet, FileText, X, Check, TrendingUp, Stethoscope, Droplets,
  Sparkles, ArrowUpRight,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  AreaChart, Area, PieChart, Pie, Cell, Legend, ComposedChart, Line,
} from "recharts";
import { toast } from "sonner";
import AwarenessGallery from "../components/AwarenessGallery";

const PIE_COLORS = ["#2B5F4E", "#D78A76", "#E8B25C", "#4A7C59", "#367661", "#C75D4A", "#9CAB8E", "#7E9C8C"];
const HYGIENE_COLORS = { poor: "#C75D4A", fair: "#E8B25C", good: "#4A7C59", excellent: "#2B5F4E" };
const GENDER_COLORS  = { Male: "#2B5F4E", Female: "#D78A76", Other: "#9CAB8E" };

const ALL_CARDS = [
  { key: "kpi",                label: "Hero & KPI Cards" },
  { key: "awareness_gallery",  label: "Recent Awareness Gallery" },
  { key: "by_date",            label: "Visit Trend" },
  { key: "by_village",         label: "Visits by Village" },
  { key: "health_conditions",  label: "Health Conditions" },
  { key: "demographics",       label: "Gender & Age Demographics" },
  { key: "hygiene",            label: "Home Hygiene" },
  { key: "awareness_by_topic", label: "Awareness Reach by Topic" },
];

// Keys introduced after the initial release — if these are missing from a user's saved prefs
// (they predate the feature), auto-enable them so the user sees them by default.
const NEWLY_INTRODUCED_KEYS = new Set(["awareness_gallery", "demographics"]);
const isNewKey = (k, saved) => NEWLY_INTRODUCED_KEYS.has(k) && !saved.includes(k);

// Friendly greeting: handle titles like "Dr." → use the next word ("Dr. Faculty" → "Dr. Faculty")
function greetingName(full) {
  if (!full) return "there";
  const parts = full.trim().split(/\s+/);
  if (!parts.length) return "there";
  const first = parts[0];
  // If first token ends with a period (e.g., "Dr."), include the following name too
  if (first.endsWith(".") && parts.length > 1) return `${first} ${parts[1]}`;
  return first;
}

export default function Dashboard() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const isFaculty = user?.role === "faculty" || user?.role === "manager";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ villages: [], date_from: "", date_to: "", batch: "", degree: "", health_status: "" });
  const [prefs, setPrefs] = useState({ visible_cards: ALL_CARDS.map((c) => c.key) });
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);

  const queryStr = useMemo(() => {
    const p = new URLSearchParams();
    filters.villages.forEach((v) => p.append("villages", v));
    if (filters.date_from) p.set("date_from", filters.date_from);
    if (filters.date_to)   p.set("date_to", filters.date_to);
    if (filters.batch)     p.set("batch", filters.batch);
    if (filters.degree)    p.set("degree", filters.degree);
    if (filters.health_status) p.set("health_status", filters.health_status);
    return p.toString();
  }, [filters]);

  const load = () => {
    setLoading(true);
    api.get(`/dashboard/summary-v2${queryStr ? `?${queryStr}` : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [queryStr]);

  useEffect(() => {
    api.get("/dashboard/prefs")
      .then((r) => {
        const saved = r.data.visible_cards || [];
        const knownSaved = new Set(saved);
        // Auto-enable any card key the user has never seen (new widgets shipped after their last save)
        const merged = ALL_CARDS.map((c) => c.key).filter((k) => knownSaved.has(k) || !saved.length || isNewKey(k, saved));
        setPrefs({ visible_cards: merged.length ? merged : ALL_CARDS.map((c) => c.key) });
      })
      .catch(() => {});
  }, []);

  const savePrefs = async (next) => {
    setPrefs(next);
    try { await api.put("/dashboard/prefs", next); } catch (e) { toast.error(formatErr(e)); }
  };
  const toggleCard = (key) => {
    const cur = new Set(prefs.visible_cards);
    cur.has(key) ? cur.delete(key) : cur.add(key);
    savePrefs({ visible_cards: ALL_CARDS.map((c) => c.key).filter((k) => cur.has(k)) });
  };

  const downloadReport = async (type) => {
    try {
      const r = await api.get(`/reports/${type}${queryStr ? `?${queryStr}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `health-report-${new Date().toISOString().slice(0, 10)}.${type === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      setDownloadOpen(false);
      toast.success(`${type.toUpperCase()} downloaded`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (loading && !data) return <div className="text-brand-muted">Loading dashboard…</div>;
  if (!data) return null;

  const t = data.totals;
  const visible = new Set(prefs.visible_cards);
  const opts = data.filter_options || { villages: [], batches: [], degrees: [], health_statuses: [] };

  // Patient/non-patient share
  const patientShare = t.people ? Math.round((t.patients / t.people) * 100) : 0;

  const kpis = [
    { label: "Households Visited",  value: t.households,         icon: Home,       color: "#2B5F4E", trend: "+visits" },
    { label: "People Surveyed",     value: t.people,             icon: Users,      color: "#4A7C59", trend: "demographics" },
    { label: "Patients Identified", value: t.patients,           icon: Stethoscope, color: "#D78A76", trend: `${patientShare}% of total` },
    { label: "Villages Covered",    value: t.villages,           icon: MapPin,     color: "#367661", trend: "footprint" },
    { label: "Awareness Programs",  value: t.awareness_programs, icon: Megaphone,  color: "#E8B25C", trend: "education" },
    { label: "People Reached",      value: t.people_reached,     icon: Activity,   color: "#C75D4A", trend: "via campaigns" },
  ];

  const healthStatusOptions = [
    "Healthy", "Type I Diabetes", "Type II Diabetes", "Hypertension", "Asthma", "Fever",
    "AGE", "Tuberculosis", "Upper Respiratory Tract Infection", "UTI", "Hypothyroidism",
    "Hyperthyroidism", "Tonsilitis",
    ...((opts.health_statuses || []).filter((x) => ![
      "Healthy","Type I Diabetes","Type II Diabetes","Hypertension","Asthma","Fever","AGE",
      "Tuberculosis","Upper Respiratory Tract Infection","UTI","Hypothyroidism","Hyperthyroidism","Tonsilitis",
    ].includes(x))),
  ];

  const hasFilters = !!(filters.villages.length || filters.date_from || filters.date_to || filters.batch || filters.degree || filters.health_status);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="dashboard-root">
      {/* ===== HERO ===== */}
      <div className="dashboard-hero relative overflow-hidden rounded-2xl text-white p-6 sm:p-8" data-testid="dashboard-hero">
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold">
              <Sparkles className="h-3 w-3" /> Community Outreach Insights
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mt-3 tracking-tight">
              Hello, {greetingName(user?.name)}.
            </h1>
            <p className="text-white/85 mt-2 text-sm sm:text-base max-w-2xl">
              {isManager
                ? "Manager view — full access to live field data, demographics, awareness reach and downloadable reports."
                : user?.role === "faculty"
                ? "Faculty view — live aggregate insights across all batches. Reports are downloadable by the Manager."
                : "Your live community health activity in one place."}
            </p>
          </div>
          {/* Hero stat */}
          <div className="grid grid-cols-2 gap-3 lg:gap-4 lg:min-w-[360px]">
            <HeroStat icon={Activity}   label="People Reached"  value={t.people_reached} sub="Across awareness drives" />
            <HeroStat icon={Heart}      label="Patients Identified" value={t.patients} sub={`${patientShare}% of surveyed`} />
          </div>
        </div>
        <div className="relative z-10 mt-6 flex flex-wrap gap-2">
          {isManager && (
            <button onClick={() => setDownloadOpen(true)} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-white text-brand-primary hover:bg-white/90 text-sm font-bold transition-colors" data-testid="reports-btn">
              <Download className="h-4 w-4" /> Download Reports
            </button>
          )}
          {isFaculty && (
            <button onClick={() => setCustomizeOpen(true)} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-white/10 hover:bg-white/20 backdrop-blur border border-white/25 text-white text-sm font-semibold transition-colors" data-testid="customize-btn">
              <Settings className="h-4 w-4" /> Customize
            </button>
          )}
        </div>
      </div>

      {/* ===== FILTER BAR ===== */}
      <div className="bg-white border border-brand-border rounded-xl p-4 shadow-tactile">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-brand-muted font-bold">Filters</div>
          {hasFilters && (
            <button onClick={() => setFilters({ villages: [], date_from: "", date_to: "", batch: "", degree: "", health_status: "" })} className="text-xs text-brand-primary font-bold hover:underline" data-testid="clear-filters-btn">
              Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <MultiSelect label="Villages" options={opts.villages} value={filters.villages} onChange={(v) => setFilters({ ...filters, villages: v })} testId="filter-villages" />
          <DateInput label="From" value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} testId="filter-from" />
          <DateInput label="To"   value={filters.date_to}   onChange={(v) => setFilters({ ...filters, date_to: v })}   testId="filter-to" />
          {isFaculty && (
            <>
              <SelectInput label="Batch"  value={filters.batch}  onChange={(v) => setFilters({ ...filters, batch: v })}  options={opts.batches}  testId="filter-batch" />
              <SelectInput label="Degree" value={filters.degree} onChange={(v) => setFilters({ ...filters, degree: v })} options={opts.degrees}  testId="filter-degree" />
            </>
          )}
          <SelectInput label="Health Status" value={filters.health_status} onChange={(v) => setFilters({ ...filters, health_status: v })} options={healthStatusOptions} testId="filter-health-status" />
        </div>
      </div>

      {/* ===== KPI ROW ===== */}
      {visible.has("kpi") && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {kpis.map((k) => (
            <div key={k.label} className="group relative overflow-hidden bg-white border border-brand-border rounded-xl p-4 hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="absolute top-0 left-0 h-1 w-full" style={{ background: k.color }} />
              <div className="flex items-center justify-between">
                <div className="h-9 w-9 rounded-lg grid place-items-center" style={{ background: `${k.color}18` }}>
                  <k.icon className="h-4 w-4" style={{ color: k.color }} />
                </div>
                <ArrowUpRight className="h-3.5 w-3.5 text-brand-muted opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="mt-3 text-3xl font-heading font-semibold tabular-nums tracking-tight">{k.value}</div>
              <div className="text-[10px] uppercase tracking-wider text-brand-muted font-bold mt-0.5">{k.label}</div>
              <div className="text-[10px] text-brand-muted mt-1.5 italic">{k.trend}</div>
            </div>
          ))}
        </div>
      )}

      {/* ===== AWARENESS GALLERY (marquee) ===== */}
      {visible.has("awareness_gallery") && (
        <SectionCard
          title="Recent Awareness Programs"
          subtitle="Last 5 — auto-scrolling. Hover to pause."
          icon={Megaphone}
          right={<Badge>{(data.recent_awareness || []).length} programs</Badge>}
          testId="recent-awareness-section"
        >
          <AwarenessGallery items={data.recent_awareness || []} />
        </SectionCard>
      )}

      {/* ===== ROW: VISIT TREND + VILLAGES ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {visible.has("by_date") && (
          <SectionCard className="lg:col-span-2" title="Visit Trend" subtitle="Daily household visits over time" icon={TrendingUp}>
            {data.by_date.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.by_date} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradVisits" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#2B5F4E" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#2B5F4E" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" vertical={false} />
                  <XAxis dataKey="date" stroke="#5C5C55" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#5C5C55" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E3DB" }} />
                  <Area type="monotone" dataKey="visits" stroke="#2B5F4E" strokeWidth={2.5} fill="url(#gradVisits)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
        )}

        {visible.has("hygiene") && (
          <SectionCard title="Home Hygiene" subtitle="Observed across visited households" icon={Droplets}>
            {data.hygiene.every((h) => h.count === 0) ? <Empty /> : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={data.hygiene} dataKey="count" nameKey="level" innerRadius={50} outerRadius={80} paddingAngle={2}>
                      {data.hygiene.map((h, i) => (<Cell key={i} fill={HYGIENE_COLORS[h.level] || PIE_COLORS[i]} />))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {data.hygiene.map((h) => (
                    <div key={h.level} className="flex items-center gap-2 text-xs">
                      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: HYGIENE_COLORS[h.level] }} />
                      <span className="capitalize text-brand-muted flex-1">{h.level}</span>
                      <span className="tabular-nums font-semibold">{h.count}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </SectionCard>
        )}
      </div>

      {/* ===== ROW: BY VILLAGE + HEALTH CONDITIONS ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {visible.has("by_village") && (
          <SectionCard title="Visits by Village" subtitle="Households surveyed + patients flagged" icon={MapPin}>
            {data.by_village.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={data.by_village.slice(0, 8)} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradHouseholds" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2B5F4E" />
                      <stop offset="100%" stopColor="#4A7C59" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" vertical={false} />
                  <XAxis dataKey="village" stroke="#5C5C55" fontSize={11} tickLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis stroke="#5C5C55" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E3DB" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="households" name="Households" fill="url(#gradHouseholds)" radius={[6, 6, 0, 0]} />
                  <Line type="monotone" dataKey="patients" name="Patients" stroke="#D78A76" strokeWidth={2.5} dot={{ r: 4, fill: "#D78A76", strokeWidth: 0 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
        )}

        {visible.has("health_conditions") && (
          <SectionCard title="Top Health Conditions" subtitle="Most reported clinical findings" icon={Stethoscope}>
            {data.health_conditions.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.health_conditions.slice(0, 8)} layout="vertical" margin={{ left: 12, right: 20 }}>
                  <defs>
                    <linearGradient id="gradHealth" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%"   stopColor="#D78A76" />
                      <stop offset="100%" stopColor="#C75D4A" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" horizontal={false} />
                  <XAxis type="number" stroke="#5C5C55" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis dataKey="condition" type="category" stroke="#5C5C55" fontSize={11} tickLine={false} width={130} />
                  <Tooltip cursor={{ fill: "#F2F0EB" }} contentStyle={{ borderRadius: 8, border: "1px solid #E5E3DB" }} />
                  <Bar dataKey="count" fill="url(#gradHealth)" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
        )}
      </div>

      {/* ===== ROW: DEMOGRAPHICS + AWARENESS BY TOPIC ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {visible.has("demographics") && (
          <SectionCard title="Gender Split" subtitle="Surveyed population" icon={Users}>
            {(!data.gender_split || data.gender_split.length === 0) ? <Empty /> : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={data.gender_split} dataKey="count" nameKey="gender" innerRadius={48} outerRadius={84} paddingAngle={2}>
                      {data.gender_split.map((g, i) => (<Cell key={i} fill={GENDER_COLORS[g.gender] || PIE_COLORS[i]} />))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-1 flex flex-wrap gap-3 justify-center text-xs">
                  {data.gender_split.map((g) => (
                    <span key={g.gender} className="inline-flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: GENDER_COLORS[g.gender] || "#9CAB8E" }} />
                      <span className="font-semibold">{g.gender}</span>
                      <span className="text-brand-muted tabular-nums">{g.count}</span>
                    </span>
                  ))}
                </div>
              </>
            )}
          </SectionCard>
        )}

        {visible.has("demographics") && (
          <SectionCard title="Age Group" subtitle="Demographic distribution" icon={Activity}>
            {(!data.age_group_split || data.age_group_split.length === 0) ? <Empty /> : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.age_group_split} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradAge" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#E8B25C" />
                      <stop offset="100%" stopColor="#C57E1F" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" vertical={false} />
                  <XAxis dataKey="group" stroke="#5C5C55" fontSize={10} tickLine={false} interval={0} angle={-25} textAnchor="end" height={60} />
                  <YAxis stroke="#5C5C55" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E3DB" }} />
                  <Bar dataKey="count" fill="url(#gradAge)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
        )}

        {visible.has("awareness_by_topic") && (
          <SectionCard title="Awareness Reach" subtitle="Attendees by topic" icon={Megaphone}>
            {data.awareness_by_topic.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={data.awareness_by_topic} dataKey="attendees" nameKey="topic" cx="50%" cy="50%" innerRadius={45} outerRadius={85} paddingAngle={2} label={(d) => d.topic} labelLine={false} fontSize={10}>
                    {data.awareness_by_topic.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
        )}
      </div>

      {/* ===== Customize Modal ===== */}
      {customizeOpen && (
        <Modal onClose={() => setCustomizeOpen(false)} title="Customize Dashboard" testId="customize-modal">
          <p className="text-sm text-brand-muted">Show or hide widgets. Your preferences are saved automatically.</p>
          <div className="mt-4 space-y-2">
            {ALL_CARDS.map((c) => (
              <label key={c.key} className="flex items-center justify-between rounded-md border border-brand-border p-3 cursor-pointer hover:bg-brand-surfaceAlt" data-testid={`toggle-${c.key}`}>
                <span className="font-medium text-sm">{c.label}</span>
                <button
                  type="button"
                  onClick={() => toggleCard(c.key)}
                  className={`h-6 w-11 rounded-full transition-colors relative ${prefs.visible_cards.includes(c.key) ? "bg-brand-primary" : "bg-brand-border"}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${prefs.visible_cards.includes(c.key) ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </label>
            ))}
          </div>
        </Modal>
      )}

      {/* ===== Reports Modal ===== */}
      {downloadOpen && (
        <Modal onClose={() => setDownloadOpen(false)} title="Download Report" testId="download-modal">
          <p className="text-sm text-brand-muted">Reports respect your current filters.</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={() => downloadReport("excel")} className="rounded-md border border-brand-border p-4 hover:border-brand-primary text-left" data-testid="download-excel-btn">
              <FileSpreadsheet className="h-7 w-7 text-[#1F7A4A]" />
              <div className="mt-3 font-heading font-semibold">Excel</div>
              <div className="text-xs text-brand-muted mt-1">4 sheets: Households, Members, Awareness, Village Summary</div>
            </button>
            <button onClick={() => downloadReport("pdf")} className="rounded-md border border-brand-border p-4 hover:border-brand-primary text-left" data-testid="download-pdf-btn">
              <FileText className="h-7 w-7 text-[#C75D4A]" />
              <div className="mt-3 font-heading font-semibold">PDF</div>
              <div className="text-xs text-brand-muted mt-1">Formatted summary with KPIs and tables</div>
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ============== Small components ============== */

function HeroStat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl bg-white/12 backdrop-blur border border-white/20 p-4">
      <div className="flex items-center gap-2 text-white/85">
        <Icon className="h-4 w-4" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold">{label}</span>
      </div>
      <div className="text-3xl sm:text-4xl font-heading font-semibold mt-2 tracking-tight tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-white/75 mt-1">{sub}</div>}
    </div>
  );
}

function SectionCard({ title, subtitle, icon: Icon, children, right, className = "", testId }) {
  return (
    <div className={`bg-white border border-brand-border rounded-xl p-5 shadow-tactile ${className}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {Icon && (
            <div className="h-9 w-9 shrink-0 rounded-lg bg-brand-surfaceAlt grid place-items-center">
              <Icon className="h-4 w-4 text-brand-primary" />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-heading font-semibold text-base truncate">{title}</div>
            {subtitle && <div className="text-xs text-brand-muted mt-0.5">{subtitle}</div>}
          </div>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-brand-surfaceAlt text-brand-primary text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 border border-brand-border">
      {children}
    </span>
  );
}

function Empty() {
  return <div className="h-[220px] grid place-items-center text-sm text-brand-muted">No data for current filters</div>;
}

function DateInput({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider text-brand-muted">{label}</label>
      <input type="date" value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-10 px-2 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none text-sm" data-testid={testId} />
    </div>
  );
}

function SelectInput({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider text-brand-muted">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-10 px-2 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none text-sm" data-testid={testId}>
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function MultiSelect({ label, options, value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const toggle = (v) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };
  return (
    <div className="relative">
      <label className="text-[10px] font-bold uppercase tracking-wider text-brand-muted">{label}</label>
      <button type="button" onClick={() => setOpen(!open)} className="mt-1 w-full h-10 px-2 rounded-md bg-brand-surfaceAlt border border-brand-border text-sm text-left flex items-center justify-between hover:bg-white" data-testid={testId}>
        <span className="truncate">{value.length ? `${value.length} selected` : "All"}</span>
        <span className="text-brand-muted">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-64 max-h-64 overflow-auto bg-white border border-brand-border rounded-md shadow-lg p-1 scrollbar-thin">
            {options.length === 0 && <div className="p-2 text-sm text-brand-muted">No villages yet</div>}
            {options.map((o) => (
              <button key={o} type="button" onClick={() => toggle(o)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-brand-surfaceAlt text-sm text-left" data-testid={`option-${o}`}>
                <span className={`h-4 w-4 rounded border ${value.includes(o) ? "bg-brand-primary border-brand-primary" : "border-brand-border"} grid place-items-center`}>
                  {value.includes(o) && <Check className="h-3 w-3 text-white" />}
                </span>
                <span className="truncate">{o}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Modal({ children, onClose, title, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid={testId}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-brand-border w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-semibold">{title}</h2>
          <button onClick={onClose} className="h-9 w-9 rounded-md hover:bg-brand-surfaceAlt grid place-items-center"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
